/**
 * The random story behind the "City at night" painting from the Art Model
 * assignment (small-art-model), scaled down to a street small enough for a
 * map toy. Each building flips a coin for office or home, gets a binomial
 * number of floors, and each floor's window is lit by a coin whose chance
 * depends on whether the building is an office. The chances are the
 * painting's; only the street's size differs.
 */

/** The painting's chances, the same as its original model. */
export const CITY_CHANCES = {
  /** Chance a building is an office. */
  office: 0.3,
  /** Chance each possible floor is built. */
  floor: 0.6,
  /** Chance an office window is lit. */
  officeLight: 0.05,
  /** Chance a home window is lit. */
  homeLight: 0.7,
} as const;

/** One building on the street. */
export interface CityBuilding {
  /** Whether the building is an office (few lit windows) or a home. */
  isOffice: boolean;
  /**
   * One entry per floor, ground floor first: whether that floor's window is
   * lit. Its length is the building's height in floors, possibly zero.
   */
  windows: boolean[];
}

/**
 * Draws a fresh street.
 *
 * @param args.buildings - How many buildings stand side by side.
 * @param args.maxFloors - How many floors a building could have; each is
 *   built with chance {@link CITY_CHANCES}.floor.
 * @param args.random - A source of uniform numbers in [0, 1). When omitted,
 *   `Math.random` is used.
 * @return The buildings, left to right.
 * @throws Never.
 */
export function sampleCityStreet({
  buildings,
  maxFloors,
  random = Math.random,
}: {
  buildings: number;
  maxFloors: number;
  random?: () => number;
}): CityBuilding[] {
  return Array.from({length: buildings}, () => {
    const isOffice = random() < CITY_CHANCES.office;
    let floors = 0;
    for (let possible = 0; possible < maxFloors; possible++) {
      if (random() < CITY_CHANCES.floor) {
        floors++;
      }
    }
    const light = isOffice ? CITY_CHANCES.officeLight : CITY_CHANCES.homeLight;
    const windows = Array.from({length: floors}, () => random() < light);
    return {isOffice, windows};
  });
}

/**
 * Counts a street's windows.
 *
 * @param street - The buildings.
 * @return How many windows are lit, and how many there are in all.
 * @throws Never.
 */
export function countCityWindows(street: readonly CityBuilding[]): {
  lit: number;
  total: number;
} {
  let lit = 0;
  let total = 0;
  street.forEach(building => {
    total += building.windows.length;
    lit += building.windows.filter(Boolean).length;
  });
  return {lit, total};
}
